import React, { useState } from 'react';
import axios from 'axios';

const ElectronicConsentModal = ({ onSuccess = () => {}, onSkip = null }) => {
  const [agreed, setAgreed] = useState(false);
  const [agreedToTOS, setAgreedToTOS] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const consentText = "Electronic Communication and Voting Consent - By checking this box and completing registration, I affirmatively consent to receive official association notices, disclosures, and voting materials electronically in accordance with applicable state homeowners' association laws and the federal Electronic Signatures in Global and National Commerce Act (E-SIGN Act, 15 U.S.C. §7001 et seq.). I understand that this consent authorizes the Association to deliver official communications, documents, and ballots via electronic transmission, including email, secure portal notifications, or other electronic means. I may withdraw this consent at any time by written notice to the Association and may request paper copies of any records in accordance with my HOA's bylaws at any time. ELECTRONIC VOTING NOTICE: Typical electronic-consent (or e-consent) rules for online voting are usually valid for a period of one year. If your HOA has signed up for electronic-voting functionality, a separate e-consent form will be required to be signed by homeowners before voting begins to participate in the electronic voting cycle. Reminder emails upon approach of expiration will be automatically delivered in compliance with applicable state laws. I acknowledge that data related to my consent — including my name, email address, property address, IP address, timestamp, and consent record — will be securely retained by the Association and/or its service provider solely to validate my consent and to comply with statutory record-retention requirements. Data will be maintained in accordance with the Association's record-keeping requirements and the Privacy Policy available at the bottom of this website. This consent remains in effect until revoked by me in writing.";
  const consentVersion = "v1.1-2025-01-15";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setError('You must agree to receive electronic communications to continue');
      return;
    }
    if (!agreedToTOS) {
      setError('You must agree to the Terms of Service to continue');
      return;
    }
    if (!agreedToPrivacy) {
      setError('You must agree to the Privacy Policy to continue');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const response = await axios.post('/api/users/electronic-consent', {
        consented: true,
        consent_text: consentText,
        consent_version: consentVersion
      });

      if (response.data.success) {
        onSuccess();
      } else {
        setError('Failed to save consent. Please try again.');
      }
    } catch (err) {
      console.error('Error saving consent:', err);
      setError(err.response?.data?.detail || 'Failed to save consent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="relative mx-auto p-8 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
            <svg className="h-10 w-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Electronic Communications Consent
          </h3>
          <p className="text-sm text-gray-600">
            To provide you with timely updates and important information
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 max-h-96 overflow-y-auto">
            <h4 className="font-bold text-gray-900 mb-4 text-lg">Electronic Communication and Voting Consent</h4>
            <div className="text-sm text-gray-800 space-y-3 leading-relaxed">
              <p>
                By checking this box and completing registration, I affirmatively consent to receive official association notices, disclosures, and voting materials electronically in accordance with applicable state homeowners' association laws and the federal <strong>Electronic Signatures in Global and National Commerce Act (E-SIGN Act, 15 U.S.C. §7001 et seq.)</strong>.
              </p>
              <p>
                I understand that this consent authorizes the Association to deliver official communications, documents, and ballots via electronic transmission, including email, secure portal notifications, or other electronic means. I may withdraw this consent at any time by written notice to the Association and may request paper copies of any records in accordance with my HOA's bylaws at any time.
              </p>
              <p className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                <strong>Electronic Voting Notice:</strong> Typical electronic-consent (or e-consent) rules for online voting are usually valid for a period of one year. If your HOA has signed up for electronic-voting functionality, a separate e-consent form will be required to be signed by homeowners before voting begins to participate in the electronic voting cycle. Reminder emails upon approach of expiration will be automatically delivered in compliance with applicable state laws.
              </p>
              <p>
                I acknowledge that data related to my consent — including my name, email address, property address, IP address, timestamp, and consent record — will be securely retained by the Association and/or its service provider solely to validate my consent and to comply with statutory record-retention requirements.
              </p>
              <p>
                Data will be maintained in accordance with the Association's record-keeping requirements and the Privacy Policy available at the bottom of this website.
              </p>
              <p className="font-semibold">
                This consent remains in effect until revoked by me in writing.
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            {/* Electronic Communications Consent */}
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
              <label className="flex items-start cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                />
                <span className="ml-3 text-sm font-semibold text-gray-900 group-hover:text-gray-700">
                  I agree to the Electronic Communication and Voting Consent above.
                </span>
              </label>
            </div>

            {/* Terms of Service */}
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
              <label className="flex items-start cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreedToTOS}
                  onChange={(e) => setAgreedToTOS(e.target.checked)}
                  className="mt-1 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                />
                <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700">
                  I agree to the{' '}
                  <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>
                </span>
              </label>
            </div>

            {/* Privacy Policy */}
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
              <label className="flex items-start cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreedToPrivacy}
                  onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                  className="mt-1 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                />
                <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700">
                  I agree to the{' '}
                  <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between">
            {onSkip && (
              <button
                type="button"
                onClick={handleSkip}
                className="text-gray-600 hover:text-gray-500 text-sm font-medium"
              >
                Skip for now
              </button>
            )}
            <button
              type="submit"
              disabled={!agreed || !agreedToTOS || !agreedToPrivacy || submitting}
              className={`ml-auto px-6 py-3 border border-transparent text-base font-medium rounded-md text-white ${
                agreed && agreedToTOS && agreedToPrivacy && !submitting
                  ? 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {submitting ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            By consenting, you agree to receive electronic communications from your HOA. 
            You can update your preferences at any time in your profile settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ElectronicConsentModal;
